import React, { useCallback, useMemo, useState } from 'react';
import styles from './addons.module.scss';
import { useEventListener } from 'ahooks';
import { Text, NumberInputGroup } from '@shakkerai/pro-components';
import { Icon } from '@shakkerai/components';
import { Menu, MenuTarget, MenuDropdown, MenuItem } from '@shakkerai/pro-components';
import { BASETYPE_VALUE_TO_LABEL, MODEL_ILLUS, MODEL_PONY, MODEL_SD15, MODEL_SDXL } from '@shakkerai/pro-models';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { Popover, Tooltip } from '@mantine/core';
import { translate } from '@shakkerai/util-i18n';

const addonList = [
  {
    name: translate('addon_fixface'),
    value: 0,
  },
  {
    name: translate('addon_fixhand'),
    value: 1,
  },
];

const addNames = {
  0: translate('addon_fixface'),
  1: translate('addon_fixhand'),
};

const mergeModeOptions = [
  { name: translate('addon_settings_mergemode_none'), value: 0 },
  { name: translate('addon_settings_mergemode_merge'), value: 1 },
  { name: translate('addon_settings_mergemode_mt'), value: 2 },
];

const mergeModeLabels = {
  0: translate('addon_settings_mergemode_none'),
  1: translate('addon_settings_mergemode_merge'),
  2: translate('addon_settings_mergemode_mt'),
};

const Item = (props) => {
  const { item, onRemove, usedAddonsMap, index, onChange } = props;
  const [settingPanelVisible, setSettingPanelVisible] = useState(false);

  const isEmpty = item.name === null;

  const handleSwitchType = useCallback(
    (value) => {
      onChange(index, { ...item, value });
    },
    [onChange, item, index],
  );

  const handleSwitchMergeMode = useCallback(
    (mergeMode) => {
      onChange(index, { ...item, mergeMode });
    },
    [onChange, item, index],
  );

  const handleUpdateEdge = useCallback(
    (edge) => {
      onChange(index, { ...item, edge });
    },
    [onChange, item, index],
  );

  const indentify = `data-indentify-${item.value}`;

  const handleGlobalClick = useCallback(
    (e) => {
      const isClickTarget = e.target.closest(`[${indentify}="true"]`);

      if (isClickTarget) return;

      setSettingPanelVisible(false);
    },
    [indentify],
  );

  useEventListener('click', handleGlobalClick);

  const renderSettingPanel = () => {
    return (
      <div
        {...{
          [indentify]: true,
        }}
      >
        <div className="flex items-center justify-between px-[16px] py-[12px]">
          <Text fw={700}>{addNames[item.value]}</Text>
          <Icon className="icon-shanchu cursor-pointer" onClick={() => setSettingPanelVisible(false)}></Icon>
        </div>
        <div className="h-[1px] bg-[#dcdcdc] w-full"></div>
        <div className="flex flex-col p-[16px] gap-[16px]">
          <div className="flex justify-between w-full">
            <Text fw={600} fSize={12}>
              {translate('addon_settings_mergemode')}
            </Text>
            <Menu>
              <MenuTarget>
                <Text fSize={12} fw={600}>
                  {mergeModeLabels[item.mergeMode] || 'Please select'}
                  <Icon className="icon-a-Group38258 ml-1" color="#222"></Icon>
                </Text>
              </MenuTarget>
              <MenuDropdown data-select>
                {mergeModeOptions.map((item) => {
                  return (
                    <MenuItem onClick={() => handleSwitchMergeMode(item.value)} key={item.value}>
                      {item.name}
                    </MenuItem>
                  );
                })}
              </MenuDropdown>
            </Menu>
          </div>
          <div className="flex justify-between w-full">
            <Text fw={600} fSize={12}>
              {translate('addon_settings_maskedge')}
            </Text>
            <div className="flex flex-1 items-center h-4 ml-3">
              <NumberInputGroup min={-128} value={item.edge} allowNegative onChange={handleUpdateEdge} max={128} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex items-center justify-between" key={item.value + '' + index}>
      <div className="flex items-center">
        <Popover opened={settingPanelVisible} width={240} position="bottom-end" shadow="md">
          <Popover.Target>
            <Tooltip label={translate('addon_settings')}>
              <Icon
                className={`icon-shapes cursor-pointer ${isEmpty ? null : styles.itemIcon}`}
                color={isEmpty ? '#CFCFCF' : '#383838'}
                fontSize={16}
                {...{
                  [indentify]: true,
                }}
                onClick={() => {
                  !isEmpty && setSettingPanelVisible(true);
                }}
              />
            </Tooltip>
          </Popover.Target>
          <Popover.Dropdown className="!p-0">{renderSettingPanel()}</Popover.Dropdown>
        </Popover>
        <Menu>
          <MenuTarget>
            <div className="flex items-center cursor-pointer">
              <Text color={isEmpty ? '#CFCFCF' : '#222'} fSize={12} className="ml-3">
                {isEmpty ? translate('addon_choose') : addNames[item.value] || item.value}
              </Text>
              <Icon className="icon-a-Group38258" color="#7A7A7A" fontSize={24} />
            </div>
          </MenuTarget>
          <MenuDropdown>
            {addonList.map((item, index) => {
              return (
                <MenuItem
                  onClick={() => handleSwitchType(item.value)}
                  key={item.value + '' + index}
                  disabled={usedAddonsMap[item.value]}
                >
                  {item.name}
                </MenuItem>
              );
            })}
          </MenuDropdown>
        </Menu>
      </div>
      <button
        onClick={() => onRemove(index)}
        className="flex items-center justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
      >
        <Icon className="icon-proshouqi" fontSize={12} color="#666" />
      </button>
    </div>
  );
};

const defaultAddOns = [];
const addonSupportedBaseTypes = [MODEL_SDXL, MODEL_ILLUS, MODEL_PONY, MODEL_SD15];

export const AddOns = React.memo((props) => {
  const { generator, onAddItem } = props;
  const addOns = generator.data?.addOns || defaultAddOns;
  const baseType = generator.data?.baseType;
  const addOnUnsupported = !addonSupportedBaseTypes.includes(baseType);

  const handleAddNewItem = useCallback(() => {
    const restAddon = addonList.filter((addon) => !addOns.some((_d) => _d.value === addon.value))?.[0];
    updateUnitData(generator.id, (params) => ({
      ...params,
      addOns: (params.addOns || []).concat({
        name: restAddon?.name || null,
        value: restAddon?.value,
        mergeMode: 1,
        edge: -4,
      }),
    }));

    onAddItem && onAddItem();
  }, [onAddItem, generator?.id, addOns]);

  const handleRemove = useCallback(
    (index) => {
      updateUnitData(generator.id, (params) => ({
        ...params,
        addOns: (params?.addOns || [])?.filter((_, i) => i !== index),
      }));
    },
    [generator?.id],
  );

  const handleChange = useCallback(
    (index, data) => {
      updateUnitData(generator.id, (params) => ({
        ...params,
        addOns: params.addOns.map((item, i) => {
          return i === index ? data : item;
        }),
      }));
    },
    [generator?.id],
  );

  const usedAddonsMap = useMemo(() => {
    if (!addOns) return {};
    return Object.fromEntries(addOns?.map((item) => [item.value, true]));
  }, [addOns]);

  const allowAddMoreItem = !addOnUnsupported && addOns && addOns.length < 2;

  return (
    <div className={`w-full bg-white`}>
      <div className="flex items-center w-full justify-between">
        <span className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          {translate('addon_title')}
        </span>
        <Tooltip
          disabled={!addOnUnsupported}
          label={translate('feature_disabled_for_sd3_2', { type: BASETYPE_VALUE_TO_LABEL[baseType] })}
        >
          <button
            onClick={handleAddNewItem}
            disabled={!allowAddMoreItem}
            className="flex items-center justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
          >
            <Icon className="icon-prozhankai" fontSize={12} color="#666" />
          </button>
        </Tooltip>
      </div>
      {addOns && addOns.length ? (
        <div className="flex flex-col gap-[16px] my-2.5">
          {addOns.map((item, index) => (
            <Item
              item={item}
              index={index}
              key={item.value + '' + index}
              onChange={handleChange}
              onRemove={handleRemove}
              usedAddonsMap={usedAddonsMap}
            />
          ))}
        </div>
      ) : (
        <div className="h-2.5" />
      )}
    </div>
  );
});
